import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * 네이티브 <select> 드롭인 대체 컴포넌트.
 * 기존 <select value onChange disabled><option/></select> 구조를 그대로 받고,
 * onChange 에는 { target: { value } } 형태로 전달하므로 핸들러 수정이 필요 없다.
 * 팝업은 position: fixed + document.body 포털이라 스크롤 컨테이너/모달 안에서도
 * 잘리거나 밀리지 않는다. (backdrop-filter/transform 이 걸린 조상은 fixed 의
 * 컨테이닝 블록이 되어 버리므로 반드시 body 로 빼야 좌표가 뷰포트 기준이 된다.)
 */
const CalmSelect = ({ value, onChange, disabled, children, className = "", style }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const options = [];
  React.Children.forEach(children, (child) => {
    if (!child || !child.props) return;
    options.push({
      value: child.props.value ?? "",
      label: child.props.children,
      disabled: Boolean(child.props.disabled),
    });
  });

  const selected = options.find((o) => String(o.value) === String(value ?? ""));

  const openList = () => {
    if (disabled || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const estHeight = Math.min(options.length * 38 + 12, 264);
    const openUp =
      rect.bottom + estHeight + 8 > window.innerHeight && rect.top > estHeight;
    setPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (ev) => {
      if (wrapRef.current?.contains(ev.target)) return;
      if (listRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    const onScroll = (ev) => {
      if (listRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const pick = (opt) => {
    if (opt.disabled) return;
    setOpen(false);
    if (onChange) onChange({ target: { value: opt.value } });
  };

  return (
    <div className={`calm-select ${className}`} style={style} ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        className={`calm-select-trigger ${open ? "is-open" : ""}`}
        onClick={() => (open ? setOpen(false) : openList())}
      >
        <span className={`calm-select-value ${selected ? "" : "is-placeholder"}`}>
          {selected ? selected.label : "선택"}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            className="calm-select-pop"
            ref={listRef}
            style={{
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
            }}
          >
            {options.map((o, i) => {
              const isSel = String(o.value) === String(value ?? "");
              return (
                <button
                  type="button"
                  key={`${o.value}-${i}`}
                  className={`calm-select-opt ${isSel ? "is-selected" : ""} ${
                    o.disabled ? "is-disabled" : ""
                  }`}
                  disabled={o.disabled}
                  onClick={() => pick(o)}
                >
                  <span>{o.label}</span>
                  {isSel && <i aria-hidden="true">✓</i>}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CalmSelect;
